import { useState } from "react";
import {
    Box, Typography, Grid, Avatar, Chip, useMediaQuery, Tabs, Tab
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { AiFillBulb } from "react-icons/ai";

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    background: "linear-gradient(160deg, #03020a 0%, #060c18 50%, #03050f 100%)",
    color: "#fff",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    paddingBottom: "60px",
    paddingTop: "32px",
}));

const SectionCard = styled(Box)(() => ({
    background: "rgba(26,14,52,0.95)",
    border: "1px solid rgba(218,165,32,0.25)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "28px",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0, left: "10%", right: "10%", height: "1px",
        background: "linear-gradient(to right,transparent,rgba(218,165,32,0.3),transparent)",
    },
}));

const MobileDataCard = styled(Box)(() => ({
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(218,165,32,0.15)",
    borderRadius: "12px",
    padding: "14px 16px",
    marginBottom: "10px",
}));

const CodeBlock = styled(Box)(() => ({
    background: "rgba(6,2,16,0.98)",
    border: "1px solid rgba(218,165,32,0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
    fontSize: "0.82rem",
    lineHeight: 1.9,
    overflowX: "auto",
    whiteSpace: "pre",
    color: "#e6edf3",
    position: "relative",
    "&::before": {
        content: '"C++"',
        position: "absolute",
        top: "8px", right: "14px",
        fontSize: "9px",
        color: "rgba(218,165,32,0.5)",
        letterSpacing: "2px",
        fontWeight: 700,
    },
}));

const TableHeader = ({ columns, cols }) => (
    <Box sx={{
        display: "grid",
        gridTemplateColumns: cols || `repeat(${columns.length},1fr)`,
        gap: "12px", padding: "12px 16px",
        bgcolor: "rgba(218,165,32,0.1)", color: "goldenrod",
        fontWeight: "bold", fontSize: "0.78rem",
        letterSpacing: "0.06em", textTransform: "uppercase",
        borderBottom: "1px solid rgba(218,165,32,0.15)",
    }}>
        {columns.map(c => <div key={c}>{c}</div>)}
    </Box>
);

const SectionHeading = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 4, mb: subtitle ? 1 : 2 }}>
        <Avatar sx={{ bgcolor: "goldenrod", color: "#100720", width: 38, height: 38, flexShrink: 0, mt: 0.3 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.45)", mt: 0.3 }}>{subtitle}</Typography>}
        </Box>
    </Box>
);

const ConceptCard = ({ title, color = "goldenrod", children }) => (
    <Box sx={{
        bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${color}33`,
        borderLeft: `3px solid ${color}`, borderRadius: "12px",
        p: "16px 18px", mb: 2,
    }}>
        {title && <Typography sx={{ fontWeight: 700, color, fontSize: "0.82rem", mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>}
        {children}
    </Box>
);

const StyledTabs = ({ value, onChange, tabs }) => (
    <Tabs value={value} onChange={onChange} variant="scrollable" scrollButtons="auto"
          sx={{
              mb: 2, borderBottom: "1px solid rgba(255,255,255,0.1)",
              "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "none", minHeight: 40 },
              "& .Mui-selected": { color: "goldenrod !important" },
              "& .MuiTabs-indicator": { bgcolor: "goldenrod" },
          }}>
        {tabs.map((t, i) => <Tab key={i} label={t} />)}
    </Tabs>
);

const PillChip = ({ label, color = "goldenrod" }) => (
    <Chip label={label} size="small" sx={{
        bgcolor: `${color}18`, color, fontWeight: 700, fontSize: "0.72rem",
        height: 22, border: `1px solid ${color}44`, "& .MuiChip-label": { px: 1 },
    }} />
);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 1: std::thread
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const threadTabs = [
    {
        label: "Creating Threads",
        code: `#include <thread>
#include <iostream>
#include <string>

// ── Create a thread from a free function ──────────────────────
void worker(int id, const std::string& msg) {
    std::cout << "Thread " << id << ": " << msg << "\\n";
}

int main() {
    // Launch: function + arguments
    std::thread t1(worker, 1, "Hello");
    std::thread t2(worker, 2, "World");

    // ── join() — wait for thread to finish ────────────────────
    t1.join();    // MUST join or detach before destructor!
    t2.join();

    // ── Lambda thread ─────────────────────────────────────────
    std::thread t3([](int n) {
        std::cout << "Lambda thread, n=" << n << "\\n";
    }, 42);
    t3.join();

    // ── detach() — let thread run independently ───────────────
    std::thread t4([]{ std::cout << "Detached\\n"; });
    t4.detach();   // we no longer own t4 — it runs until done
    // WARNING: ensure detached thread doesn't use dangling references!

    // ── joinable() — check before join/detach ─────────────────
    std::thread t5(worker, 5, "check");
    if (t5.joinable()) t5.join();   // safe

    // ── hardware_concurrency — number of logical cores ────────
    unsigned cores = std::thread::hardware_concurrency();
    std::cout << "CPU cores: " << cores << "\\n";
}`,
    },
    {
        label: "Thread Arguments",
        code: `#include <thread>
#include <iostream>
#include <string>

// ── Arguments are COPIED into the thread by default ──────────
void printValue(int n) { std::cout << n << "\\n"; }

int x = 42;
std::thread t1(printValue, x);   // copy of x passed
x = 99;                           // changing x doesn't affect thread
t1.join();                        // still prints 42

// ── Pass by reference: use std::ref() ────────────────────────
void increment(int& val) { ++val; }

int counter = 0;
std::thread t2(increment, std::ref(counter));   // pass by reference!
t2.join();
std::cout << counter << "\\n";   // 1 — modified!

// ── Pass by const ref: std::cref() ────────────────────────────
void printMsg(const std::string& msg) { std::cout << msg << "\\n"; }
std::string s = "Hello";
std::thread t3(printMsg, std::cref(s));
t3.join();

// ── Member function on object ─────────────────────────────────
struct Worker {
    void run(int id) { std::cout << "Worker " << id << "\\n"; }
};

Worker w;
std::thread t4(&Worker::run, &w, 1);  // &method, object_ptr, args...
t4.join();

// ── Move thread ───────────────────────────────────────────────
std::thread t5(printValue, 10);
std::thread t6 = std::move(t5);   // t5 is now empty — transfer ownership
// t5.join();  // ❌ not joinable after move
t6.join();    // ✅`,
    },
    {
        label: "RAII Thread (jthread)",
        code: `#include <thread>
#include <iostream>
#include <stdexcept>

// ── Problem: forgetting join() on exception ────────────────────
// std::thread destructor calls std::terminate() if thread is joinable!

void dangerousVersion() {
    std::thread t([]{ std::cout << "working...\\n"; });
    doSomething();        // if this throws → t.~thread() → std::terminate!
    t.join();             // never reached if exception thrown
}

// ── RAII thread guard ─────────────────────────────────────────
class ThreadGuard {
    std::thread& t;
public:
    explicit ThreadGuard(std::thread& t) : t(t) {}
    ~ThreadGuard() {
        if (t.joinable()) t.join();   // always joins on destruction
    }
    ThreadGuard(const ThreadGuard&)            = delete;
    ThreadGuard& operator=(const ThreadGuard&) = delete;
};

void safeVersion() {
    std::thread t([]{ std::cout << "working...\\n"; });
    ThreadGuard guard(t);            // join guaranteed on scope exit
    doSomething();                   // even if this throws, t is joined
}

// ── C++20: std::jthread — auto-joins on destruction ──────────
#include <stop_token>

void modernVersion() {
    std::jthread t([](std::stop_token st){
        while (!st.stop_requested()) {
            std::cout << "Running...\\n";
            std::this_thread::sleep_for(std::chrono::milliseconds(100));
        }
    });
    std::this_thread::sleep_for(std::chrono::seconds(1));
    // t.request_stop() called automatically when jthread is destroyed
    // then auto-joins — no manual join needed!
}   // ← t destroyed here: stop requested + auto-joined`,
    },
    {
        label: "Thread ID & Sleep",
        code: `#include <thread>
#include <chrono>
#include <iostream>

// ── Thread IDs ────────────────────────────────────────────────
std::thread t([](){
    // get own ID from inside thread:
    std::thread::id myId = std::this_thread::get_id();
    std::cout << "Thread id: " << myId << "\\n";
});

std::thread::id tid = t.get_id();   // get ID from outside
std::cout << "Launched thread: " << tid << "\\n";
t.join();

// Main thread ID:
std::cout << "Main: " << std::this_thread::get_id() << "\\n";

// ── Sleep ─────────────────────────────────────────────────────
using namespace std::chrono_literals;

std::this_thread::sleep_for(200ms);            // sleep for duration
std::this_thread::sleep_for(std::chrono::seconds(1));

auto deadline = std::chrono::steady_clock::now() + 500ms;
std::this_thread::sleep_until(deadline);        // sleep until time point

// ── yield — hint to reschedule ────────────────────────────────
// Tells OS scheduler: "I'm not doing critical work right now"
std::this_thread::yield();

// ── Spin wait with yield ───────────────────────────────────────
std::atomic<bool> ready{false};
std::thread producer([&]{ ready.store(true); });
while (!ready.load())
    std::this_thread::yield();   // don't busy-spin without yielding!
producer.join();`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 2: Mutexes & Locks
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const mutexTabs = [
    {
        label: "mutex & lock_guard",
        code: `#include <thread>
#include <mutex>
#include <iostream>
#include <vector>

// ── Race condition without mutex ──────────────────────────────
int counter = 0;
void unsafeIncrement() {
    for (int i = 0; i < 100000; ++i)
        ++counter;   // DATA RACE — undefined behaviour!
}

// ── Protected with mutex ──────────────────────────────────────
std::mutex mtx;
int safeCounter = 0;

void safeIncrement() {
    for (int i = 0; i < 100000; ++i) {
        std::lock_guard<std::mutex> lock(mtx);  // RAII: locks on creation
        ++safeCounter;                           // only one thread here
    }   // ← lock released automatically (destructor)
}

int main() {
    std::vector<std::thread> threads;
    for (int i = 0; i < 4; ++i)
        threads.emplace_back(safeIncrement);
    for (auto& t : threads)
        t.join();
    std::cout << safeCounter << "\\n";   // always 400000

    // ── C++17: Class Template Argument Deduction ──────────────
    std::lock_guard lock(mtx);   // deduces std::lock_guard<std::mutex>

    // ── Manual lock/unlock (avoid — prefer RAII) ──────────────
    mtx.lock();
    // critical section
    mtx.unlock();   // must call even if exception — use lock_guard!

    // ── try_lock — non-blocking ────────────────────────────────
    if (mtx.try_lock()) {
        // got the lock
        mtx.unlock();
    } else {
        // couldn't acquire — do something else
    }
}`,
    },
    {
        label: "unique_lock & timed",
        code: `#include <mutex>
#include <shared_mutex>
#include <chrono>
#include <iostream>

std::mutex mtx;
std::timed_mutex timedMtx;
std::shared_mutex rwMtx;

// ── unique_lock — more flexible than lock_guard ───────────────
void flexibleSection() {
    std::unique_lock<std::mutex> lock(mtx);   // locked immediately

    // Can unlock early and re-lock:
    lock.unlock();    // release lock temporarily
    doExpensiveWork();
    lock.lock();      // re-acquire
    updateSharedState();
}   // ← auto-unlocked at end of scope

// ── Deferred locking ─────────────────────────────────────────
void deferredLock() {
    std::unique_lock<std::mutex> lock(mtx, std::defer_lock);
    // ... prepare work without holding lock ...
    lock.lock();    // explicitly lock later
    // critical section
}

// ── Timed mutex — try for a duration ─────────────────────────
bool trySection() {
    using namespace std::chrono_literals;
    std::unique_lock<std::timed_mutex> lock(timedMtx, 100ms);
    if (!lock) {
        std::cout << "Couldn't acquire lock in 100ms\\n";
        return false;
    }
    doWork();
    return true;
}

// ── shared_mutex — multiple readers, single writer (RWLock) ───
std::string sharedData;

void reader() {
    std::shared_lock<std::shared_mutex> lock(rwMtx);  // shared (read) lock
    std::cout << sharedData << "\\n";   // many readers simultaneously OK
}

void writer(const std::string& data) {
    std::unique_lock<std::shared_mutex> lock(rwMtx);  // exclusive (write) lock
    sharedData = data;   // exclusive — no other reader/writer active
}`,
    },
    {
        label: "Deadlock Prevention",
        code: `#include <mutex>
#include <thread>
#include <iostream>

std::mutex mtxA, mtxB;

// ── Classic deadlock: two threads locking in opposite order ───
void thread1_bad() {
    std::lock_guard a(mtxA);   // locks A
    std::this_thread::sleep_for(std::chrono::milliseconds(1));
    std::lock_guard b(mtxB);   // waits for B... but thread2 has B!
}
void thread2_bad() {
    std::lock_guard b(mtxB);   // locks B
    std::this_thread::sleep_for(std::chrono::milliseconds(1));
    std::lock_guard a(mtxA);   // waits for A... but thread1 has A! DEADLOCK
}

// ── Fix 1: always lock in the same order ─────────────────────
void thread1_fixed() {
    std::lock_guard a(mtxA);   // always A then B
    std::lock_guard b(mtxB);
}
void thread2_fixed() {
    std::lock_guard a(mtxA);   // same order — no deadlock
    std::lock_guard b(mtxB);
}

// ── Fix 2: std::lock — lock multiple at once (atomic) ─────────
void bothAtOnce() {
    std::unique_lock<std::mutex> a(mtxA, std::defer_lock);
    std::unique_lock<std::mutex> b(mtxB, std::defer_lock);
    std::lock(a, b);   // locks both atomically — no deadlock possible!
    // critical section using both a and b
}

// ── Fix 3: C++17 scoped_lock — simplest ───────────────────────
void scopedVersion() {
    std::scoped_lock lock(mtxA, mtxB);   // locks both atomically, RAII
    // critical section
}   // ← both unlocked

// ── Deadlock detection tips ───────────────────────────────────
// 1. Always lock multiple mutexes with scoped_lock or std::lock
// 2. Never hold a lock while calling user code (callbacks)
// 3. Never hold a lock while waiting for another lock
// 4. Use lock hierarchies: always acquire in order of ID`,
    },
    {
        label: "once_flag & call_once",
        code: `#include <mutex>
#include <iostream>
#include <memory>

// ── std::call_once — thread-safe one-time initialisation ──────
std::once_flag initFlag;
std::unique_ptr<HeavyResource> resource;

void initResource() {
    resource = std::make_unique<HeavyResource>();
    std::cout << "Resource initialised\\n";
}

void useResource() {
    // initResource called EXACTLY ONCE across all threads:
    std::call_once(initFlag, initResource);
    resource->doWork();
}

// Multiple threads calling useResource:
// Only ONE will run initResource, others wait then proceed.

// ── Thread-safe Singleton using call_once ─────────────────────
class Database {
    static std::unique_ptr<Database> instance;
    static std::once_flag flag;
    Database() { std::cout << "DB connected\\n"; }
public:
    static Database& getInstance() {
        std::call_once(flag, [](){
            instance = std::make_unique<Database>();
        });
        return *instance;
    }
    void query(const std::string& sql) { /* ... */ }
};
std::unique_ptr<Database> Database::instance;
std::once_flag Database::flag;

// ── Lazy static (C++11 guarantee — simpler) ───────────────────
Database& getDB() {
    static Database db;   // ← C++11 guarantees this is thread-safe!
    return db;
}
// Static local variables are initialised exactly once, thread-safely.
// Prefer this over call_once for simple cases.`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 3: Condition Variables
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const condvarTabs = [
    {
        label: "Basic Usage",
        code: `#include <thread>
#include <mutex>
#include <condition_variable>
#include <iostream>

std::mutex mtx;
std::condition_variable cv;
bool ready = false;

// ── Producer: sets state and notifies ─────────────────────────
void producer() {
    std::this_thread::sleep_for(std::chrono::seconds(1));
    {
        std::lock_guard<std::mutex> lock(mtx);
        ready = true;
        std::cout << "Producer: data ready\\n";
    }   // ← lock released before notify (best practice)
    cv.notify_one();   // wake ONE waiting thread
    // cv.notify_all(); // wake ALL waiting threads
}

// ── Consumer: waits for the condition ─────────────────────────
void consumer() {
    std::unique_lock<std::mutex> lock(mtx);   // unique_lock required!
    // ── wait with predicate — protects against spurious wakeups ─
    cv.wait(lock, []{ return ready; });
    //  ↑ equivalent to:
    //  while (!ready) cv.wait(lock);   // re-checks after each wakeup
    std::cout << "Consumer: processing data\\n";
    // lock is re-acquired before wait returns
}

int main() {
    std::thread prod(producer);
    std::thread cons(consumer);
    prod.join();
    cons.join();
}

// ── Why the predicate matters ─────────────────────────────────
// Threads can wake spuriously (OS quirk) even without notify!
// Without predicate: consumer might see ready==false and continue.
// With predicate:    consumer re-checks and goes back to sleep if false.`,
    },
    {
        label: "Producer-Consumer Queue",
        code: `#include <thread>
#include <mutex>
#include <condition_variable>
#include <queue>
#include <optional>
#include <iostream>

template<typename T>
class ThreadSafeQueue {
    std::queue<T>            queue;
    mutable std::mutex       mtx;
    std::condition_variable  cv;
    bool                     closed = false;
public:
    void push(T item) {
        {
            std::lock_guard<std::mutex> lock(mtx);
            queue.push(std::move(item));
        }
        cv.notify_one();
    }

    // Returns value, or nullopt if closed and empty
    std::optional<T> pop() {
        std::unique_lock<std::mutex> lock(mtx);
        cv.wait(lock, [this]{ return !queue.empty() || closed; });
        if (queue.empty()) return std::nullopt;   // closed
        T item = std::move(queue.front());
        queue.pop();
        return item;
    }

    void close() {
        { std::lock_guard<std::mutex> lock(mtx); closed = true; }
        cv.notify_all();   // wake all consumers so they can exit
    }

    size_t size() const {
        std::lock_guard<std::mutex> lock(mtx);
        return queue.size();
    }
};

int main() {
    ThreadSafeQueue<int> q;
    std::thread producer([&]{
        for (int i = 0; i < 10; ++i) { q.push(i); }
        q.close();
    });
    std::thread consumer([&]{
        while (auto val = q.pop())
            std::cout << "Got: " << *val << "\\n";
    });
    producer.join();
    consumer.join();
}`,
    },
    {
        label: "Wait with Timeout",
        code: `#include <mutex>
#include <condition_variable>
#include <chrono>
#include <iostream>

std::mutex mtx;
std::condition_variable cv;
bool dataReady = false;

// ── wait_for — wait at most a duration ────────────────────────
void timedConsumer() {
    std::unique_lock<std::mutex> lock(mtx);
    using namespace std::chrono_literals;

    auto status = cv.wait_for(lock, 500ms, []{ return dataReady; });
    //  status: true  = condition became true (predicate returned true)
    //          false = timeout expired (predicate still false)

    if (status) {
        std::cout << "Data arrived in time\\n";
    } else {
        std::cout << "Timeout — using default\\n";
    }
}

// ── wait_until — wait until a specific time point ─────────────
void deadlineConsumer() {
    std::unique_lock<std::mutex> lock(mtx);
    auto deadline = std::chrono::steady_clock::now()
                    + std::chrono::milliseconds(200);

    bool ok = cv.wait_until(lock, deadline, []{ return dataReady; });
    if (!ok) std::cout << "Deadline missed\\n";
}

// ── Typical heartbeat / watchdog pattern ──────────────────────
void watchdog(std::condition_variable& cv,
              std::mutex& mtx,
              std::atomic<bool>& alive) {
    using namespace std::chrono_literals;
    std::unique_lock<std::mutex> lock(mtx);
    while (alive) {
        if (cv.wait_for(lock, 1s) == std::cv_status::timeout) {
            std::cout << "Watchdog: worker stalled!\\n";
            // take corrective action
        }
    }
}`,
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 4: Atomics
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const atomicTabs = [
    {
        label: "std::atomic Basics",
        code: `#include <atomic>
#include <thread>
#include <iostream>
#include <vector>

// ── std::atomic — lock-free thread-safe primitive ─────────────
// Reads and writes are indivisible (no partial updates)

std::atomic<int>  counter{0};
std::atomic<bool> flag{false};

void worker() {
    for (int i = 0; i < 100000; ++i)
        ++counter;      // ✅ atomic increment — no mutex needed!
}

int main() {
    std::vector<std::thread> threads;
    for (int i = 0; i < 4; ++i)
        threads.emplace_back(worker);
    for (auto& t : threads) t.join();
    std::cout << counter << "\\n";   // always 400000

    // ── Atomic operations ─────────────────────────────────────
    counter.store(0);                     // write
    int val = counter.load();             // read
    counter.fetch_add(5);                 // add 5, return old value
    counter.fetch_sub(2);                 // subtract 2
    counter.fetch_or(0xFF);               // bitwise OR (integers only)
    counter.fetch_and(0x0F);              // bitwise AND

    // ── exchange — write new, return old ──────────────────────
    int old = counter.exchange(42);       // atomic swap

    // ── compare_exchange — CAS (Compare-And-Swap) ─────────────
    int expected = 42;
    bool swapped = counter.compare_exchange_strong(expected, 100);
    // if counter == 42: sets counter=100, swapped=true
    // if counter != 42: sets expected=current value, swapped=false

    // ── Atomic flag — simplest lock-free primitive ────────────
    std::atomic_flag spinlock = ATOMIC_FLAG_INIT;
    // test_and_set: sets flag, returns old value
    while (spinlock.test_and_set(std::memory_order_acquire)) {}  // spin
    // critical section
    spinlock.clear(std::memory_order_release);  // release
}`,
    },
    {
        label: "Memory Ordering",
        code: `#include <atomic>
#include <thread>

// ── Memory orders — control visibility of operations ─────────
//
//  memory_order_relaxed   — no synchronisation, only atomicity
//  memory_order_acquire   — this load sees all stores before the release
//  memory_order_release   — all stores before this are visible to acquire
//  memory_order_acq_rel   — both acquire and release (for RMW ops)
//  memory_order_seq_cst   — total sequential order (default, safest)
//
//  Sequential consistency (seq_cst) is the default — correct but
//  may generate more memory fences on ARM/PowerPC architectures.

std::atomic<bool> dataReady{false};
int data = 0;                         // NOT atomic — protected by ordering

void producer() {
    data = 42;                              // write data BEFORE flag
    dataReady.store(true,
        std::memory_order_release);         // "release" — publishes data
}

void consumer() {
    while (!dataReady.load(
        std::memory_order_acquire)) {}      // "acquire" — sees released data
    // Guaranteed: data == 42 here
    std::cout << data << "\\n";
}

// ── Relaxed ordering — only atomicity, no synchronisation ────
std::atomic<int> stats{0};
void countEvent() {
    stats.fetch_add(1, std::memory_order_relaxed);  // just count — no sync needed
}

// ── Rule of thumb ─────────────────────────────────────────────
// Start with seq_cst (default). Only use weaker orderings
// after profiling shows a bottleneck, with careful analysis.`,
    },
    {
        label: "Lock-Free Patterns",
        code: `#include <atomic>
#include <optional>
#include <memory>
#include <iostream>

// ── Spinlock using atomic_flag ────────────────────────────────
class Spinlock {
    std::atomic_flag flag = ATOMIC_FLAG_INIT;
public:
    void lock() {
        while (flag.test_and_set(std::memory_order_acquire))
            std::this_thread::yield();  // yield during spin (don't hog CPU)
    }
    void unlock() {
        flag.clear(std::memory_order_release);
    }
};

// ── Lock-free counter with CAS ────────────────────────────────
class SafeCounter {
    std::atomic<int> value{0};
public:
    void increment() {
        int expected = value.load();
        while (!value.compare_exchange_weak(expected, expected + 1))
            ;  // retry — CAS failed (someone else updated)
        // compare_exchange_weak may fail spuriously (prefer in loops)
    }
    int get() const { return value.load(); }
};

// ── Atomic shared_ptr (C++20) ─────────────────────────────────
// Before C++20: use std::atomic_load/store on shared_ptr
// C++20: std::atomic<std::shared_ptr<T>> is specialised

struct Node { int val; std::shared_ptr<Node> next; };
std::atomic<std::shared_ptr<Node>> head;

void push(int val) {
    auto node = std::make_shared<Node>();
    node->val = val;
    node->next = head.load();
    while (!head.compare_exchange_weak(node->next, node))
        ;  // retry until our CAS wins
}

// ── Atomic bool as a stop flag ────────────────────────────────
std::atomic<bool> running{true};
void workerLoop() {
    while (running.load(std::memory_order_relaxed)) {
        doWork();
    }
}
void stopWorker() { running.store(false); }`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 5: Futures & Async
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const futureTabs = [
    {
        label: "std::async & future",
        code: `#include <future>
#include <iostream>
#include <vector>
#include <numeric>

// ── std::async — run work asynchronously, get result via future ─
std::future<int> f = std::async(std::launch::async, [](){
    // runs in a new thread (or thread pool)
    return computeHeavyResult();
});

// Do other work while computation runs...
doOtherWork();

// Get the result (blocks until ready):
int result = f.get();    // waits if not done yet
std::cout << result << "\\n";
// f.get() can only be called ONCE — after that future is invalid

// ── Launch policies ───────────────────────────────────────────
auto f1 = std::async(std::launch::async,     task);  // always new thread
auto f2 = std::async(std::launch::deferred,  task);  // lazy — runs on get()
auto f3 = std::async(std::launch::async |
                     std::launch::deferred,  task);  // implementation decides
auto f4 = std::async(task);  // same as async | deferred (implementation choice)

// ── Parallel computation example ──────────────────────────────
std::vector<int> data(10'000'000);
std::iota(data.begin(), data.end(), 1);

auto mid = data.begin() + data.size() / 2;
auto f_left  = std::async(std::launch::async, [&]{
    return std::accumulate(data.begin(), mid, 0LL);
});
auto f_right = std::async(std::launch::async, [&]{
    return std::accumulate(mid, data.end(), 0LL);
});
long long total = f_left.get() + f_right.get();

// ── Check readiness without blocking ─────────────────────────
if (f1.wait_for(std::chrono::milliseconds(0)) == std::future_status::ready)
    std::cout << "Ready: " << f1.get() << "\\n";`,
    },
    {
        label: "promise & shared_future",
        code: `#include <future>
#include <thread>
#include <iostream>

// ── promise: manually set a future's value from another thread ─
std::promise<int> prom;
std::future<int>  fut = prom.get_future();

std::thread producer([&prom](){
    int result = computeResult();
    prom.set_value(result);        // fulfil the promise
});

// Consumer waits on future:
int val = fut.get();               // blocks until set_value called
std::cout << val << "\\n";
producer.join();

// ── Set exception via promise ─────────────────────────────────
std::promise<int> p2;
auto f2 = p2.get_future();
std::thread t([&p2](){
    try {
        throw std::runtime_error("compute failed");
    } catch (...) {
        p2.set_exception(std::current_exception());
    }
});
t.join();
try {
    f2.get();     // re-throws the exception
} catch (const std::exception& e) {
    std::cerr << "Error: " << e.what() << "\\n";
}

// ── shared_future — multiple threads can get the same value ───
std::promise<std::string> sp;
std::shared_future<std::string> sf = sp.get_future().share();

// Multiple threads can call sf.get():
auto t1 = std::async([sf]{ std::cout << sf.get() << "\\n"; });
auto t2 = std::async([sf]{ std::cout << sf.get() << "\\n"; });
sp.set_value("broadcast result");
t1.get(); t2.get();`,
    },
    {
        label: "packaged_task",
        code: `#include <future>
#include <thread>
#include <queue>
#include <functional>
#include <iostream>

// ── packaged_task — wrap a callable + its future ──────────────
// Lets you defer calling a function and retrieve its result later

int compute(int x, int y) { return x * y + 1; }

std::packaged_task<int(int, int)> task(compute);
std::future<int> result = task.get_future();

// Execute the task (can be on a different thread):
std::thread t(std::move(task), 6, 7);   // passes args 6 and 7
t.join();
std::cout << result.get() << "\\n";    // 43

// ── Simple thread pool task queue ────────────────────────────
class SimplePool {
    std::queue<std::packaged_task<void()>> tasks;
    std::mutex mtx;
    std::condition_variable cv;
    std::vector<std::thread> workers;
    bool stop = false;
public:
    SimplePool(int n) {
        for (int i = 0; i < n; ++i)
            workers.emplace_back([this]{
                while (true) {
                    std::unique_lock<std::mutex> lock(mtx);
                    cv.wait(lock, [this]{ return !tasks.empty() || stop; });
                    if (stop && tasks.empty()) return;
                    auto task = std::move(tasks.front()); tasks.pop();
                    lock.unlock();
                    task();
                }
            });
    }

    template<typename F>
    auto submit(F&& fn) -> std::future<std::invoke_result_t<F>> {
        std::packaged_task<std::invoke_result_t<F>()> task(std::forward<F>(fn));
        auto future = task.get_future();
        { std::lock_guard<std::mutex> lock(mtx);
          tasks.emplace(std::move(task)); }
        cv.notify_one();
        return future;
    }

    ~SimplePool() {
        { std::lock_guard<std::mutex> lock(mtx); stop = true; }
        cv.notify_all();
        for (auto& w : workers) w.join();
    }
};`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DATA — Section 6: Thread Pool & Parallel Patterns
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const patternTabs = [
    {
        label: "Thread Pool",
        code: `#include <thread>
#include <mutex>
#include <condition_variable>
#include <queue>
#include <functional>
#include <future>
#include <vector>

class ThreadPool {
    std::vector<std::thread>          workers;
    std::queue<std::function<void()>> tasks;
    std::mutex                        queueMtx;
    std::condition_variable           condition;
    bool                              stop{false};

public:
    explicit ThreadPool(size_t n) {
        for (size_t i = 0; i < n; ++i)
            workers.emplace_back([this] {
                while (true) {
                    std::function<void()> task;
                    {
                        std::unique_lock<std::mutex> lock(queueMtx);
                        condition.wait(lock,
                            [this]{ return stop || !tasks.empty(); });
                        if (stop && tasks.empty()) return;
                        task = std::move(tasks.front());
                        tasks.pop();
                    }
                    task();   // execute outside the lock
                }
            });
    }

    // Enqueue a task, return a future for its result
    template<typename F, typename... Args>
    auto enqueue(F&& f, Args&&... args)
        -> std::future<std::invoke_result_t<F, Args...>>
    {
        using RetType = std::invoke_result_t<F, Args...>;
        auto task = std::make_shared<std::packaged_task<RetType()>>(
            std::bind(std::forward<F>(f), std::forward<Args>(args)...)
        );
        auto future = task->get_future();
        { std::lock_guard<std::mutex> lock(queueMtx);
          tasks.emplace([task]{ (*task)(); }); }
        condition.notify_one();
        return future;
    }

    ~ThreadPool() {
        { std::lock_guard<std::mutex> lock(queueMtx); stop = true; }
        condition.notify_all();
        for (auto& w : workers) w.join();
    }
};

// Usage:
ThreadPool pool(4);
auto f1 = pool.enqueue([](int x){ return x * x; }, 10);
auto f2 = pool.enqueue([](int a, int b){ return a + b; }, 3, 4);
std::cout << f1.get() << " " << f2.get() << "\\n";  // 100 7`,
    },
    {
        label: "Parallel Algorithms (C++17)",
        code: `#include <algorithm>
#include <execution>
#include <vector>
#include <numeric>
#include <iostream>

std::vector<int> v(10'000'000);
std::iota(v.begin(), v.end(), 1);

// ── Execution policies (C++17) ────────────────────────────────
//   std::execution::seq       — sequential (default, no parallelism)
//   std::execution::par       — parallel (multiple threads)
//   std::execution::par_unseq — parallel + vectorised (SIMD)
//   std::execution::unseq     — vectorised only (C++20)

// ── Parallel sort ─────────────────────────────────────────────
std::sort(std::execution::par, v.begin(), v.end());

// ── Parallel sort with custom comparator ─────────────────────
std::sort(std::execution::par_unseq, v.begin(), v.end(),
    [](int a, int b){ return a > b; });   // descending

// ── Parallel transform ────────────────────────────────────────
std::vector<double> results(v.size());
std::transform(std::execution::par_unseq,
    v.begin(), v.end(), results.begin(),
    [](int x){ return std::sqrt(static_cast<double>(x)); });

// ── Parallel reduce (sum) ─────────────────────────────────────
long long sum = std::reduce(std::execution::par,
    v.begin(), v.end(), 0LL);

// ── Parallel count_if ─────────────────────────────────────────
long evens = std::count_if(std::execution::par,
    v.begin(), v.end(), [](int x){ return x % 2 == 0; });

// ── Parallel for_each ─────────────────────────────────────────
std::atomic<long long> total{0};
std::for_each(std::execution::par,
    v.begin(), v.end(),
    [&total](int x){ total.fetch_add(x, std::memory_order_relaxed); });

// ── Important: parallel lambdas must not share non-atomic state!
// ❌ int bad = 0; for_each(par,...,[&bad](int x){ bad += x; }); // RACE`,
    },
    {
        label: "Semaphore (C++20)",
        code: `#include <semaphore>
#include <thread>
#include <iostream>
#include <vector>

// ── std::counting_semaphore — limit concurrent access ─────────
// Like a mutex but allows N threads simultaneously

std::counting_semaphore<4> pool(4);  // max 4 concurrent accesses

void limitedWorker(int id) {
    pool.acquire();          // decrement — blocks when count reaches 0
    std::cout << "Worker " << id << " running\\n";
    std::this_thread::sleep_for(std::chrono::milliseconds(100));
    pool.release();          // increment — wakes a waiting thread
}

// ── Binary semaphore — single permit (like mutex but signallable) ─
std::binary_semaphore signal(0);   // starts at 0 (no permit)

void waitForSignal() {
    signal.acquire();   // blocks until release is called
    std::cout << "Signal received!\\n";
}

void giveSignal() {
    std::this_thread::sleep_for(std::chrono::milliseconds(500));
    signal.release();   // wakes the waiter
}

// ── Connection pool example ───────────────────────────────────
class ConnectionPool {
    std::counting_semaphore<10> sem;
    std::vector<Connection>     conns;
    std::mutex                  mtx;
    size_t                      nextIdx{0};
public:
    ConnectionPool() : sem(10) { /* create 10 connections */ }

    Connection& acquire() {
        sem.acquire();                       // wait for available slot
        std::lock_guard<std::mutex> lk(mtx);
        return conns[nextIdx++ % conns.size()];
    }
    void release() { sem.release(); }
};

int main() {
    std::vector<std::thread> workers;
    for (int i = 0; i < 10; ++i)
        workers.emplace_back(limitedWorker, i);
    for (auto& t : workers) t.join();
}`,
    },
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// REFERENCE TABLES & CHEATSHEET DATA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const syncRows = [
    { prim: "std::mutex",             hdr: "<mutex>",              use: "Mutual exclusion — one thread at a time",             note: "Use with lock_guard / unique_lock" },
    { prim: "std::shared_mutex",      hdr: "<shared_mutex>",       use: "Multiple readers OR single writer (RW lock)",         note: "shared_lock for read, unique_lock for write" },
    { prim: "std::timed_mutex",       hdr: "<mutex>",              use: "Mutex with try_lock_for / try_lock_until",            note: "Use when you can't wait indefinitely" },
    { prim: "std::recursive_mutex",   hdr: "<mutex>",              use: "Same thread can lock multiple times",                 note: "Avoid — usually indicates design issue" },
    { prim: "std::lock_guard",        hdr: "<mutex>",              use: "RAII mutex: lock on create, unlock on destroy",       note: "Prefer for simple scoped locks" },
    { prim: "std::unique_lock",       hdr: "<mutex>",              use: "RAII + unlock/relock + deferred + timed",             note: "Required for condition_variable" },
    { prim: "std::scoped_lock",       hdr: "<mutex>",              use: "Lock multiple mutexes atomically (C++17)",            note: "Prevents deadlock on multi-mutex acquire" },
    { prim: "std::condition_variable",hdr: "<condition_variable>", use: "Wait for a condition; notify when ready",             note: "Always use with predicate to avoid spurious" },
    { prim: "std::atomic<T>",         hdr: "<atomic>",             use: "Lock-free atomic reads/writes for integral types",    note: "fetch_add, CAS, exchange operations" },
    { prim: "std::future / promise",  hdr: "<future>",             use: "Single-use channel for async result",                 note: "get() blocks; can only be called once" },
    { prim: "std::shared_future",     hdr: "<future>",             use: "Multiple threads receive same result",                note: "get() can be called from many threads" },
    { prim: "std::counting_semaphore",hdr: "<semaphore>",          use: "Limit concurrency to N threads (C++20)",              note: "acquire/release; more flexible than mutex" },
];

const cheatBlocks = [
    {
        title: "Thread Basics", color: "#f0883e",
        items: [
            "✅ Always join() or detach() before thread destructor",
            "✅ Pass by ref with std::ref(), const ref with std::cref()",
            "✅ Use std::jthread (C++20) for auto-join on destruction",
            "✅ Check joinable() before calling join() or detach()",
            "✅ std::thread::hardware_concurrency() for core count",
            "❌ Detached threads must not reference stack variables",
            "❌ Don't call join() on a moved-from thread",
            "❌ Forgetting join/detach causes std::terminate!",
        ],
    },
    {
        title: "Mutex & Locking", color: "#58a6ff",
        items: [
            "✅ Always use RAII: lock_guard, unique_lock, scoped_lock",
            "✅ scoped_lock for locking multiple mutexes at once",
            "✅ unique_lock required for condition_variable",
            "✅ shared_mutex for read-heavy, write-rare data",
            "✅ Release lock before notify_all/notify_one",
            "❌ Never call user code while holding a lock",
            "❌ Never lock multiple mutexes separately (deadlock risk)",
            "❌ Recursive lock usually signals a design flaw",
        ],
    },
    {
        title: "Atomics & Memory", color: "#7ee787",
        items: [
            "✅ std::atomic for simple counters/flags — no mutex needed",
            "✅ Default seq_cst is safest; weaken only after profiling",
            "✅ acquire on load, release on store for producer-consumer",
            "✅ compare_exchange_weak in loops, strong for single attempt",
            "✅ atomic_flag for simple spinlocks",
            "❌ Don't assume atomic = sequentially consistent by default on all platforms",
            "❌ Relaxed order provides no synchronisation — only atomicity",
            "❌ Don't protect complex multi-step ops with atomics — use mutex",
        ],
    },
    {
        title: "Common Bugs", color: "#f85149",
        items: [
            "❌ Data race: two threads access same var, at least one writes",
            "❌ Deadlock: thread A waits for B, B waits for A",
            "❌ Spurious wakeup: always use predicate with condition_variable",
            "❌ Dangling reference: [&] lambda outliving captured thread",
            "❌ Forgetting std::ref: argument copied instead of referenced",
            "❌ Double-checked locking without atomics — broken pre-C++11",
            "❌ std::future::get() called twice — throws std::future_error",
            "❌ Sharing non-atomic state in parallel algorithms",
        ],
    },
];
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function CppMultithreading() {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [thrTab,  setThrTab]  = useState(0);
    const [mtxTab,  setMtxTab]  = useState(0);
    const [cvTab,   setCvTab]   = useState(0);
    const [atmTab,  setAtmTab]  = useState(0);
    const [futTab,  setFutTab]  = useState(0);
    const [patTab,  setPatTab]  = useState(0);

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Box sx={{ textAlign: "center", mb: 5, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{
                    fontWeight: 800, color: "goldenrod", display: "inline-block",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    boxShadow: "0 6px 20px rgba(218,165,32,0.3)",
                    border: "1px solid rgba(218,165,32,0.25)", letterSpacing: "0.04em",
                }}>
                    C++ MULTITHREADING
                </Typography>
                <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                    std::thread · mutex · condition_variable · atomic · future · thread pool · C++20
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 2 }}>
                    {["std::thread","mutex","lock_guard","condition_variable","atomic","future/promise","jthread","semaphore"].map(t => (
                        <Chip key={t} label={t} size="small" sx={{
                            bgcolor: "rgba(218,165,32,0.1)", color: "rgba(218,165,32,0.85)",
                            border: "1px solid rgba(218,165,32,0.25)", fontSize: "0.72rem",
                        }} />
                    ))}
                </Box>
            </Box>

            {/* ── Intro Banner ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ bgcolor: "rgba(218,165,32,0.06)", border: "1px solid rgba(218,165,32,0.2)", borderRadius: "14px", p: "18px 24px", display: "flex", gap: 2, alignItems: "flex-start" }}>
                    <Typography sx={{ fontSize: "1.4rem", flexShrink: 0 }}>💡</Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.85, color: "rgba(255,255,255,0.8)" }}>
                        C++11 introduced a <strong style={{ color: "goldenrod" }}>portable thread model</strong> with{" "}
                        <code style={{ color: "goldenrod" }}>std::thread</code>, mutexes, atomics, and futures.
                        C++17 added <strong style={{ color: "goldenrod" }}>parallel STL algorithms</strong>.
                        C++20 brought <code style={{ color: "goldenrod" }}>jthread</code>,{" "}
                        <code style={{ color: "goldenrod" }}>semaphore</code>, and{" "}
                        <code style={{ color: "goldenrod" }}>latch/barrier</code>. Include{" "}
                        <code style={{ color: "goldenrod" }}>&lt;thread&gt;</code>,{" "}
                        <code style={{ color: "goldenrod" }}>&lt;mutex&gt;</code>,{" "}
                        <code style={{ color: "goldenrod" }}>&lt;atomic&gt;</code>,{" "}
                        <code style={{ color: "goldenrod" }}>&lt;future&gt;</code>.
                    </Typography>
                </Box>
            </Box>

            {/* ── Overview cards ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, mb: 4 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                    {[
                        { icon: "🧵", title: "Threads",       color: "#f0883e", points: ["std::thread — portable thread", "join() / detach() lifecycle", "std::jthread (C++20) auto-joins", "pass args by value or std::ref"] },
                        { icon: "🔒", title: "Synchronisation",color: "#58a6ff", points: ["mutex + lock_guard / unique_lock", "scoped_lock — multiple mutexes", "condition_variable — wait/notify", "call_once — one-time init"] },
                        { icon: "⚛️", title: "Lock-Free",     color: "#7ee787", points: ["std::atomic — indivisible ops", "fetch_add, CAS, exchange", "memory_order controls visibility", "atomic_flag — simple spinlock"] },
                    ].map((card, i) => (
                        <Box key={i} sx={{ bgcolor: "rgba(26,14,52,0.9)", border: `1px solid ${card.color}44`, borderTop: `3px solid ${card.color}`, borderRadius: "14px", p: "16px 18px" }}>
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
                                <Typography sx={{ fontSize: "1.4rem" }}>{card.icon}</Typography>
                                <Typography sx={{ fontWeight: 800, color: card.color, fontSize: "0.9rem" }}>{card.title}</Typography>
                            </Box>
                            {card.points.map((pt, j) => (
                                <Typography key={j} sx={{ fontSize: "0.8rem", opacity: 0.75, lineHeight: 1.7 }}>• {pt}</Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>

                    {/* ════════════════════════════════════════════════════
              SECTION 1 — std::thread
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            1. std::thread
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::thread</code> launches a new OS thread.
                            Every thread must be either <code style={{ color: "goldenrod" }}>join()</code>ed
                            (wait for completion) or <code style={{ color: "goldenrod" }}>detach()</code>ed
                            before its destructor — otherwise the program calls{" "}
                            <code style={{ color: "goldenrod" }}>std::terminate()</code>.
                        </Typography>

                        <ConceptCard title="Thread Lifecycle" color="#f0883e">
                            <CodeBlock sx={{ "&::before": { content: '""' } }}>{`//  std::thread t(fn, args...);   ← thread starts immediately
//       │
//       ├── t.join()             ← THIS thread waits for t to finish
//       │                           t becomes non-joinable after join
//       │
//       └── t.detach()           ← t runs independently; we lose handle
//                                   t becomes non-joinable after detach
//
//  t.~thread() with joinable t  → std::terminate()!  ALWAYS join or detach.
//  C++20: std::jthread            → auto-joins in destructor (prefer this)`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={thrTab} onChange={(_, v) => setThrTab(v)} tabs={threadTabs.map(t => t.label)} />
                        <CodeBlock>{threadTabs[thrTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 2 — MUTEXES & LOCKS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            2. Mutexes &amp; Locks
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <strong>mutex</strong> ensures only one thread executes a critical section
                            at a time. Always use RAII wrappers —{" "}
                            <code style={{ color: "goldenrod" }}>lock_guard</code>,{" "}
                            <code style={{ color: "goldenrod" }}>unique_lock</code>, or{" "}
                            <code style={{ color: "goldenrod" }}>scoped_lock</code> — so locks are
                            released even when exceptions are thrown.
                        </Typography>

                        <ConceptCard title="RAII Lock Hierarchy" color="#58a6ff">
                            <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 2 }}>
                                {[
                                    { name: "lock_guard",  color: "#58a6ff", points: ["Simplest RAII lock", "Locks on construction", "No unlock/relock", "Use for most cases"] },
                                    { name: "unique_lock", color: "#f0883e", points: ["Flexible RAII lock", "Can unlock + relock", "Required for cond_var", "Timed locking support"] },
                                    { name: "scoped_lock", color: "#7ee787", points: ["Multi-mutex RAII (C++17)", "Atomic acquisition", "Prevents deadlock", "Replaces std::lock"] },
                                ].map((item, j) => (
                                    <Box key={j} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${item.color}33`, borderRadius: "10px", p: "12px 14px" }}>
                                        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: item.color, fontSize: "0.84rem", mb: 0.75 }}>{item.name}</Typography>
                                        {item.points.map((pt, k) => (
                                            <Typography key={k} sx={{ fontSize: "0.78rem", opacity: 0.7, lineHeight: 1.65 }}>• {pt}</Typography>
                                        ))}
                                    </Box>
                                ))}
                            </Box>
                        </ConceptCard>

                        <StyledTabs value={mtxTab} onChange={(_, v) => setMtxTab(v)} tabs={mutexTabs.map(t => t.label)} />
                        <CodeBlock>{mutexTabs[mtxTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 3 — CONDITION VARIABLES
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            3. Condition Variables
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            A <code style={{ color: "goldenrod" }}>condition_variable</code> lets a
                            thread <strong>sleep</strong> until another thread changes state and
                            calls <code style={{ color: "goldenrod" }}>notify</code>. Always use a
                            predicate with <code style={{ color: "goldenrod" }}>wait()</code> to
                            guard against <strong>spurious wakeups</strong>.
                        </Typography>

                        <ConceptCard title="Spurious Wakeups — Always Use a Predicate" color="#f85149">
                            <CodeBlock sx={{ "&::before": { content: '""' } }}>{`// ❌ WRONG — no predicate:
cv.wait(lock);                  // may wake spuriously when ready is still false!

// ✅ CORRECT — with predicate:
cv.wait(lock, []{ return ready; });  // re-checks ready after every wakeup
// Equivalent to: while (!ready) cv.wait(lock);`}</CodeBlock>
                        </ConceptCard>

                        <StyledTabs value={cvTab} onChange={(_, v) => setCvTab(v)} tabs={condvarTabs.map(t => t.label)} />
                        <CodeBlock>{condvarTabs[cvTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 4 — ATOMICS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            4. std::atomic &amp; Memory Ordering
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Atomic operations are <strong>indivisible</strong> — no other thread can
                            observe a partial state. They are typically lock-free on modern hardware
                            and avoid the overhead of a mutex for simple counters, flags, and
                            producer-consumer patterns.
                        </Typography>

                        <StyledTabs value={atmTab} onChange={(_, v) => setAtmTab(v)} tabs={atomicTabs.map(t => t.label)} />
                        <CodeBlock>{atomicTabs[atmTab].code}</CodeBlock>

                        {/* Memory order table */}
                        <SectionHeading icon={<AiFillBulb size={18} />} title="Memory Order Quick Reference" subtitle="Ordered from weakest to strongest" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Order", "Strength", "Meaning", "Use When"]} cols="160px 80px 1.5fr 1.5fr" />}
                            {[
                                { o: "relaxed",  s: "Weakest", m: "Atomicity only — no sync with other ops",          u: "Simple counters where ordering doesn't matter" },
                                { o: "consume",  s: "Weak",    m: "Data dependency ordering (mostly deprecated)",      u: "Rarely used; use acquire instead" },
                                { o: "acquire",  s: "Medium",  m: "See all stores before matching release",            u: "Load side of producer-consumer sync" },
                                { o: "release",  s: "Medium",  m: "All prior stores visible to matching acquire",      u: "Store side of producer-consumer sync" },
                                { o: "acq_rel",  s: "Strong",  m: "Both acquire and release (for RMW operations)",     u: "fetch_add, CAS in the middle of a chain" },
                                { o: "seq_cst",  s: "Strongest", m:"Total order visible to all threads (default)",        u: "Default — use unless profiling shows bottleneck" },
                            ].map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                                            <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.83rem" }}>{row.o}</Typography>
                                            <PillChip label={row.s} color={row.s === "Strongest" ? "#7ee787" : row.s === "Weakest" ? "#f85149" : "#58a6ff"} />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.79rem", opacity: 0.7, mb: 0.3 }}>{row.m}</Typography>
                                        <Typography sx={{ fontSize: "0.77rem", color: "rgba(255,255,255,0.4)" }}>{row.u}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "160px 80px 1.5fr 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.81rem" }}>memory_order_{row.o}</Box>
                                        <PillChip label={row.s} color={row.s === "Strongest" ? "#7ee787" : row.s === "Weakest" ? "#f85149" : "#58a6ff"} />
                                        <Box sx={{ fontSize: "0.8rem", opacity: 0.75 }}>{row.m}</Box>
                                        <Box sx={{ fontSize: "0.78rem", opacity: 0.55 }}>{row.u}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 5 — FUTURES & ASYNC
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            5. Futures, Promises &amp; Async
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            <code style={{ color: "goldenrod" }}>std::future</code> represents a value
                            that will be available later. <code style={{ color: "goldenrod" }}>std::async</code>
                            {" "}is the simplest way to run work asynchronously.{" "}
                            <code style={{ color: "goldenrod" }}>promise</code> lets you manually fulfil a
                            future from any thread.
                        </Typography>

                        <StyledTabs value={futTab} onChange={(_, v) => setFutTab(v)} tabs={futureTabs.map(t => t.label)} />
                        <CodeBlock>{futureTabs[futTab].code}</CodeBlock>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              SECTION 6 — THREAD POOL & PARALLEL PATTERNS
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            6. Thread Pool &amp; Parallel Patterns
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.88, lineHeight: 1.85 }}>
                            Creating threads per-task is expensive. A <strong>thread pool</strong> pre-creates
                            worker threads and dispatches tasks via a queue. C++17{" "}
                            <strong>parallel algorithms</strong> provide parallel sort, transform, and reduce
                            without manual thread management. C++20 <strong>semaphores</strong> control
                            concurrent access limits.
                        </Typography>

                        <StyledTabs value={patTab} onChange={(_, v) => setPatTab(v)} tabs={patternTabs.map(t => t.label)} />
                        <CodeBlock>{patternTabs[patTab].code}</CodeBlock>

                        {/* Sync primitives reference table */}
                        <SectionHeading icon={<span style={{ fontSize: 18 }}>🔧</span>} title="Synchronisation Primitives Reference" />
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {!isMobile && <TableHeader columns={["Primitive", "Header", "Use For", "Notes"]} cols="1.4fr 1fr 1.5fr 1.5fr" />}
                            {syncRows.map((row, i) =>
                                isMobile ? (
                                    <MobileDataCard key={i}>
                                        <Box sx={{ display: "flex", gap: 1, mb: 0.4 }}>
                                            <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.82rem" }}>{row.prim}</Typography>
                                            <PillChip label={row.hdr} color="#a855f7" />
                                        </Box>
                                        <Typography sx={{ fontSize: "0.8rem", opacity: 0.7, mb: 0.3 }}>{row.use}</Typography>
                                        <Typography sx={{ fontSize: "0.77rem", color: "rgba(255,255,255,0.4)" }}>{row.note}</Typography>
                                    </MobileDataCard>
                                ) : (
                                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1.5fr 1.5fr", gap: "12px", px: 2, py: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)", alignItems: "center", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "rgba(218,165,32,0.04)" } }}>
                                        <Box sx={{ fontFamily: "monospace", fontWeight: 700, color: "goldenrod", fontSize: "0.79rem" }}>{row.prim}</Box>
                                        <PillChip label={row.hdr} color="#a855f7" />
                                        <Box sx={{ fontSize: "0.8rem", opacity: 0.75 }}>{row.use}</Box>
                                        <Box sx={{ fontSize: "0.78rem", opacity: 0.55 }}>{row.note}</Box>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionCard>

                    {/* ════════════════════════════════════════════════════
              CHEATSHEET
          ════════════════════════════════════════════════════ */}
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={3}>
                            Multithreading Cheatsheet
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 3, mb: 4 }}>
                            {cheatBlocks.map((block, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${block.color}33`, borderRadius: "14px", p: "18px 20px" }}>
                                    <Typography sx={{ fontWeight: 800, color: block.color, fontSize: "0.88rem", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{block.title}</Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.65 }}>
                                        {block.items.map((item, j) => (
                                            <Typography key={j} sx={{ fontSize: "0.81rem", opacity: 0.82, lineHeight: 1.6 }}>{item}</Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Syntax quick reference */}
                        <Typography sx={{ fontWeight: 800, color: "goldenrod", fontSize: "0.88rem", mb: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Syntax Quick Reference
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 2 }}>
                            {[
                                { title: "Launch & join threads",  color: "#f0883e", code: `std::thread t(fn, arg1, arg2);
t.join();   // wait
// Or RAII (C++20):
std::jthread jt(fn, arg); // auto-joins` },
                                { title: "Mutex critical section", color: "#58a6ff", code: `std::mutex mtx;
{
  std::lock_guard lock(mtx);
  // critical section
}  // auto unlock` },
                                { title: "Condition variable",     color: "#7ee787", code: `std::condition_variable cv;
// Notify:
{ lock_guard lk(mtx); ready=true; }
cv.notify_one();
// Wait:
unique_lock lk(mtx);
cv.wait(lk, []{ return ready; });` },
                                { title: "Async future",           color: "#a855f7", code: `auto f = std::async(
  std::launch::async,
  [](int x){ return x*x; }, 7
);
int result = f.get();  // 49` },
                            ].map((item, i) => (
                                <Box key={i} sx={{ bgcolor: "rgba(255,255,255,0.025)", border: `1px solid ${item.color}33`, borderRadius: "12px", overflow: "hidden" }}>
                                    <Box sx={{ bgcolor: `${item.color}12`, px: 2, py: 1, borderBottom: `1px solid ${item.color}22` }}>
                                        <Typography sx={{ fontWeight: 700, color: item.color, fontSize: "0.82rem" }}>{item.title}</Typography>
                                    </Box>
                                    <CodeBlock sx={{ borderRadius: 0, border: "none", fontSize: "0.79rem", p: "12px 16px", "&::before": { content: '""' } }}>
                                        {item.code}
                                    </CodeBlock>
                                </Box>
                            ))}
                        </Box>
                    </SectionCard>

                </Grid>
            </Grid>
        </PageWrapper>
    );
}
